import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import StartEndAddressFields from './Fields/StartEndAddressFields'
import StartEndDateTimeFields from './Fields/StartEndDateTimeFields'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import type { CreateTrainItemViewModel } from '../Create/models'
import type { PatchTrainItemViewModel } from '../Edit/models'

import type { AutocompletePlaceParam } from 'tripPlanner/types/google'

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = [
  'train_station',
  'bus_station',
  'transit_station',
  'travel_agency',
  'route',
]

interface Props {
  tripId: string
}

function TrainFields({ tripId }: Props) {
  const { urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateTrainItemViewModel | PatchTrainItemViewModel
  >()
  const { control, watch, register } = methods
  const watchBooked = watch('isBooked')

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const departureDateLabel = intl.formatMessage({
    id: 'q7OwA2qx35JheDSQ7lhi',
    defaultMessage: 'Departure date',
    description: 'Form label for departure date field in train trip item editor',
  })

  const arrivalDateLabel = intl.formatMessage({
    id: 'qPovnYDQ4y_etdjmHEq_',
    defaultMessage: 'Arrival date',
    description: 'Form label for arrival date field in train trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'DF3Mj9-0ql0iG2u-vC8Z',
    defaultMessage: 'Operator',
    description: 'Form label for operator name field in train trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'GWLjnIfrQRERFpsyiyEH',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in train trip item editor',
  })

  const confirmationNumberLabel = intl.formatMessage({
    id: 'smN8xu1QaxEkQ_c79AMW',
    defaultMessage: 'Confirmation number',
    description: 'Form label for booking confirmation number in train trip item editor',
  })

  const seatLabel = intl.formatMessage({
    id: 'AXRl_-EBbdtFrSO69i3T',
    defaultMessage: 'Seat',
    description: 'Form label for seat field in train trip item editor',
  })

  const seatPlaceholder = intl.formatMessage({
    id: 'u74-OXgikjPW3P7C1Z6O',
    defaultMessage: 'Seat number',
    description: 'Placeholder text for seat number input in train trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'F3X_qUYmiElkBtUl3JOr',
    defaultMessage: 'Price',
    description: 'Form label for price field in train trip item editor',
  })

  const additionalNotesLabel = intl.formatMessage({
    id: '0j6dmcL2qRExUVwF5OXD',
    defaultMessage: 'Additional notes',
    description: 'Form label for additional notes field in train trip item editor',
  })

  const notesPlaceholder = intl.formatMessage({
    id: 'rX51YbvoPocJ5gbFxNet',
    defaultMessage: 'Add notes about this booking',
    description: 'Placeholder text for notes textarea in train trip item editor',
  })

  return (
    <>
      <StartEndAddressFields
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        tripId={tripId}
      />
      <StartEndDateTimeFields
        startDateLabel={departureDateLabel}
        endDateLabel={arrivalDateLabel}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
      />

      <Input
        {...register('operatorName')}
        label={operatorLabel}
        placeholder={operatorLabel}
      />

      <Input
        {...register('url', { ...urlRegisterConfig })}
        label={websiteLabel}
        placeholder={websiteLabel}
      />

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      {watchBooked && (
        <>
          <Input
            {...register('confirmationCode')}
            label={confirmationNumberLabel}
            placeholder={confirmationNumberLabel}
          />
          <Input {...register('seat')} label={seatLabel} placeholder={seatPlaceholder} />
        </>
      )}

      <Input
        {...register('price', { ...priceRegisterConfig })}
        label={priceLabel}
        placeholder={priceLabel}
        {...priceFieldProps}
      />

      <TextArea
        {...register('notes')}
        placeholder={notesPlaceholder}
        label={additionalNotesLabel}
        resize="vertical"
      />
    </>
  )
}

export default TrainFields
